import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Label, cn } from '@ideoz/ui';
import {
  Check,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  Monitor,
  Smartphone,
  Globe,
  type LucideIcon,
} from 'lucide-react';
import ColorPicker from './ColorPicker';
import { DesignSystemSectionNav } from './DesignSystemSectionNav';
import { DesignSystemLivePreview } from './DesignSystemLivePreview';
import { UnsavedChangesDot } from './UnsavedChangesDot';
import { MultiPillOptionGroup, PillOptionGroup } from './PillOptionGroup';
import { PatternGroup } from './PatternGroup';
import { CornerRadiusSelector } from './CornerRadiusSelector';
import { FontFamilyPicker } from './FontFamilyPicker';
import type {
  DesignSystem,
  UxPatterns,
  WebsiteUxPatterns,
  Platform,
  MobileOs,
} from './DesignSystemPanel';
import { DEFAULT_MOBILE_OS } from '../designSystemPayload';
import {
  COLOR_PALETTES,
  DENSITY_OPTIONS,
  ELEVATION_PILLS,
  elevationToShadows,
  FONT_PAIRINGS,
  getActiveFontPairingId,
  getActivePaletteId,
  CONTAINER_WIDTH_OPTIONS,
  DELETE_PATTERN_OPTIONS,
  DETAIL_PATTERN_OPTIONS,
  EMPTY_STATE_PATTERN_OPTIONS,
  FILTER_PATTERN_OPTIONS,
  FORM_PATTERN_OPTIONS,
  LIST_PATTERN_OPTIONS,
  LOADING_PATTERN_OPTIONS,
  NAV_PATTERN_OPTIONS,
  SEARCH_PATTERN_OPTIONS,
  THEME_COLOR_PRESETS,
  THEME_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  LANGUAGE_OPTIONS,
  PLATFORM_OPTIONS,
  MOBILE_OS_OPTIONS,
  MOBILE_CONVENTIONS,
  WEBSITE_NAV_OPTIONS,
  WEBSITE_CONTENT_LAYOUT_OPTIONS,
  WEBSITE_PRIMARY_CTA_OPTIONS,
  WEBSITE_HERO_OPTIONS,
  WEBSITE_LIST_DISPLAY_OPTIONS,
  WEBSITE_SEARCH_OPTIONS,
  type DesignSystemSectionId,
} from '../designSystemConstants';
import { loadGoogleFontPreview } from '../googleFonts';

// Brand marks for the mobile OS chooser (lucide ships no real Apple/Android
// logos). `currentColor` so they inherit the chip's text colour.
function AppleLogo({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function AndroidLogo({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.57-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25S8.25 13.31 8.25 14 7.69 15.25 7 15.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
    </svg>
  );
}

type DesignSystemPageLayoutProps = {
  designSystem: DesignSystem;
  setDesignSystem: React.Dispatch<React.SetStateAction<DesignSystem>>;
  previewUrl: string | null;
  isDirty: boolean;
  saving: boolean;
  isExtracting: boolean;
  extractFiles: File[];
  extractPreviews: string[];
  isDragOver: boolean;
  sectionRefs: Record<DesignSystemSectionId, React.RefObject<HTMLElement>>;
  centerScrollRef: React.RefObject<HTMLDivElement>;
  onSectionClick: (id: DesignSystemSectionId) => void;
  onExtractClick: () => void;
  onChooseScreenshots: () => void;
  onExtract: () => void;
  onRemoveExtractFile: (idx: number) => void;
  onLogoClick: () => void;
  onLogoRemove: (e?: React.MouseEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onSave: () => void;
  onReset: () => void;
  hasUpdatedDesignSystem: boolean;
};

function SectionBlock({
  id,
  sectionRef,
  title,
  description,
  children,
}: {
  id: DesignSystemSectionId;
  sectionRef: React.RefObject<HTMLElement>;
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      id={id}
      ref={sectionRef}
      className="scroll-mt-4 rounded-2xl border border-gray-200/70 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="mb-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && (
          <p className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

export function DesignSystemPageLayout({
  designSystem,
  setDesignSystem,
  previewUrl,
  isDirty,
  saving,
  isExtracting,
  extractFiles,
  extractPreviews,
  isDragOver,
  sectionRefs,
  centerScrollRef,
  onSectionClick,
  onExtractClick,
  onChooseScreenshots,
  onExtract,
  onRemoveExtractFile,
  onLogoClick,
  onLogoRemove,
  onDragEnter,
  onDragLeave,
  onDrop,
  onSave,
  onReset,
  hasUpdatedDesignSystem,
}: DesignSystemPageLayoutProps): JSX.Element {
  const { t } = useTranslation();
  // Translate an option list's labels by group+value, falling back to the
  // English label when a key is missing (see `designSystem.opt.*`).
  const tOpts = <T extends { value: string; label: string }>(group: string, opts: T[]): T[] =>
    opts.map((o) => ({
      ...o,
      label: t(`designSystem.opt.${group}.${o.value}`, { defaultValue: o.label }),
    }));
  const activePaletteId = getActivePaletteId(designSystem);
  const activeFontId = getActiveFontPairingId(designSystem);
  const ux = designSystem.uxPatterns ?? {};

  // Product type is multi-select: the user can generate for several targets at
  // once (e.g. web app + mobile). It persists as a comma-joined string in the
  // single `projectType` field so the backend column/DTO stay unchanged — a
  // single selection is written as a bare value (keeps ai-api mode detection).
  const selectedProductTypes = (designSystem.projectType ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const toggleProductType = (value: string) => {
    const next = new Set(selectedProductTypes);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    const joined = PRODUCT_TYPE_OPTIONS.filter((option) => next.has(option.value))
      .map((option) => option.value)
      .join(',');
    setDesignSystem((s) => ({ ...s, projectType: joined }));
  };

  const setUx = <K extends keyof UxPatterns>(key: K, value: UxPatterns[K]) => {
    setDesignSystem((s) => ({
      ...s,
      uxPatterns: { ...(s.uxPatterns ?? {}), [key]: value },
    }));
  };

  // Which platform's UX layer is being edited / previewed (brand/colors/type are
  // shared). `web_app` edits `uxPatterns`, `website` edits `websiteUxPatterns`,
  // `mobile` derives its conventions from `mobileOs`.
  const activePlatform: Platform = designSystem.activePlatform ?? 'web_app';
  const mobileOs: MobileOs = designSystem.mobileOs ?? DEFAULT_MOBILE_OS;
  const websiteUx = designSystem.websiteUxPatterns ?? {};
  const setActivePlatform = (value: Platform) =>
    setDesignSystem((s) => ({ ...s, activePlatform: value }));
  const setMobileOs = (value: MobileOs) => setDesignSystem((s) => ({ ...s, mobileOs: value }));
  const setWebsiteUx = <K extends keyof WebsiteUxPatterns>(key: K, value: WebsiteUxPatterns[K]) => {
    setDesignSystem((s) => ({
      ...s,
      websiteUxPatterns: { ...(s.websiteUxPatterns ?? {}), [key]: value },
    }));
  };
  const platformLabel = (p: Platform) =>
    t(`designSystem.opt.platform.${p}`, {
      defaultValue: PLATFORM_OPTIONS.find((o) => o.value === p)?.label ?? p,
    });
  const platformIcons: Record<Platform, LucideIcon> = {
    web_app: Monitor,
    mobile: Smartphone,
    website: Globe,
  };

  const applyPalette = (palette: (typeof COLOR_PALETTES)[number]) => {
    setDesignSystem((s) => ({
      ...s,
      primaryColor: palette.primary,
      secondaryColor: palette.accent,
      primaryForeground: palette.primaryForeground,
      secondaryForeground: palette.secondaryForeground,
    }));
  };

  const applyFontPairing = (pairing: (typeof FONT_PAIRINGS)[number]) => {
    setDesignSystem((s) => ({
      ...s,
      fontFamily: pairing.body,
      headingFontFamily: pairing.heading,
    }));
  };

  const applyTheme = (theme: NonNullable<DesignSystem['theme']>) => {
    setDesignSystem((s) => ({
      ...s,
      theme,
      ...THEME_COLOR_PRESETS[theme],
    }));
  };

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      <aside className="hidden w-[220px] shrink-0 border-e border-gray-100 px-5 py-6 dark:border-gray-800 xl:block">
        <DesignSystemSectionNav onSectionClick={onSectionClick} onExtractClick={onExtractClick} />
      </aside>

      <div
        ref={centerScrollRef}
        className="flex min-h-0 min-w-0 flex-1 justify-center overflow-y-auto overscroll-contain bg-gray-50/60 dark:bg-gray-950/40"
      >
        <div className="h-full w-full max-w-2xl px-6 py-6">
          <header className="mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('designSystem.pageTitle')}
              </h2>
              {isDirty && <UnsavedChangesDot />}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              {t('designSystem.pageDesc')}
            </p>
          </header>

          <div className="space-y-4 pb-6">
            <SectionBlock
              id="brand"
              sectionRef={sectionRefs.brand}
              title={t('designSystem.brandTitle')}
              description={t('designSystem.brandDesc')}
            >
              <div className="space-y-4">
                <div className="rounded-xl border border-primary-100/80 bg-primary-50/30 p-4 dark:border-primary-500/15 dark:bg-primary-500/5">
                  <div className="mb-1 flex items-center gap-2">
                    <Sparkles className="size-4 text-primary-600" />
                    <Label className="text-sm font-semibold text-primary-700 dark:text-primary-300 mb-0">
                      {t('designSystem.extractTitle')}
                    </Label>
                  </div>
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    {t('designSystem.extractHint')}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onChooseScreenshots}
                      disabled={isExtracting}
                    >
                      {extractFiles.length === 0
                        ? t('designSystem.chooseScreenshots')
                        : t('designSystem.addMore')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={onExtract}
                      disabled={isExtracting || extractFiles.length === 0}
                    >
                      {isExtracting ? t('designSystem.extracting') : t('designSystem.extract')}
                    </Button>
                    {extractFiles.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {t('designSystem.selectedCount', { count: extractFiles.length })}
                      </span>
                    )}
                  </div>
                  {extractPreviews.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {extractPreviews.map((url, idx) => (
                        <div
                          key={url}
                          className="relative size-16 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700"
                        >
                          <img
                            src={url}
                            alt={`Screenshot ${idx + 1}`}
                            className="size-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => onRemoveExtractFile(idx)}
                            aria-label={`Remove screenshot ${idx + 1}`}
                            disabled={isExtracting}
                            className="absolute end-0.5 top-0.5 rounded-full bg-white/90 p-0.5 shadow hover:bg-white disabled:opacity-50"
                          >
                            <X className="size-3 text-gray-700" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Logo
                  </Label>
                  <div
                    onClick={onLogoClick}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    className={cn(
                      'mt-2 cursor-pointer rounded-xl border border-dashed transition-colors',
                      isDragOver
                        ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10'
                        : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/50'
                    )}
                  >
                    {previewUrl ? (
                      <div className="flex items-center gap-3 p-3">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 dark:border-gray-700">
                          <img
                            src={previewUrl}
                            alt="Logo preview"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                            Logo uploaded
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('designSystem.clickToReplace')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLogoRemove(e);
                          }}
                          aria-label={t('designSystem.removeLogo')}
                          className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-gray-400 shadow-sm dark:bg-gray-800">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <path
                              d="M5.69547 27.6372L14.4909 18.8418C15.0189 18.3138 15.2829 18.0498 15.5873 17.9509C15.8551 17.8639 16.1436 17.8639 16.4114 17.9509C16.7158 18.0498 16.9798 18.3138 17.5078 18.8418L26.2446 27.5786M18.666 20L22.4909 16.1752C23.0189 15.6471 23.2829 15.3831 23.5873 15.2842C23.8551 15.1972 24.1436 15.1972 24.4114 15.2842C24.7158 15.3831 24.9798 15.6471 25.5078 16.1752L29.3327 20M13.3327 12C13.3327 13.4728 12.1388 14.6667 10.666 14.6667C9.19326 14.6667 7.99935 13.4728 7.99935 12C7.99935 10.5272 9.19326 9.33333 10.666 9.33333C12.1388 9.33333 13.3327 10.5272 13.3327 12ZM9.06602 28H22.9327C25.1729 28 26.293 28 27.1486 27.564C27.9013 27.1805 28.5132 26.5686 28.8967 25.816C29.3327 24.9603 29.3327 23.8402 29.3327 21.6V10.4C29.3327 8.15979 29.3327 7.03969 28.8967 6.18404C28.5132 5.43139 27.9013 4.81947 27.1486 4.43597C26.293 4 25.1729 4 22.9327 4H9.06602C6.82581 4 5.7057 4 4.85005 4.43597C4.09741 4.81947 3.48548 5.43139 3.10199 6.18404C2.66602 7.03969 2.66602 8.15979 2.66602 10.4V21.6C2.66602 23.8402 2.66602 24.9603 3.10199 25.816C3.48548 26.5686 4.09741 27.1805 4.85005 27.564C5.7057 28 6.82581 28 9.06602 28Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1 text-start">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t('designSystem.uploadLogo')}
                          </p>
                          <p className="text-xs text-gray-400">{t('designSystem.logoHint')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('designSystem.language')}
                  </Label>
                  <p className="mb-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('designSystem.languageHint')}
                  </p>
                  <PillOptionGroup
                    options={tOpts('language', LANGUAGE_OPTIONS)}
                    value={designSystem.language}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, language: v }))}
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="colors"
              sectionRef={sectionRefs.colors}
              title={t('designSystem.colorsTitle')}
              description={t('designSystem.colorsDesc')}
            >
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {COLOR_PALETTES.map((palette) => {
                  const selected = activePaletteId === palette.id;
                  return (
                    <button
                      key={palette.id}
                      type="button"
                      onClick={() => applyPalette(palette)}
                      className={cn(
                        'relative rounded-xl border p-3 text-start transition-colors',
                        selected
                          ? 'border-primary-500 ring-1 ring-primary-500/30'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                      )}
                    >
                      {selected && (
                        <span className="absolute end-2 top-2 flex size-5 items-center justify-center rounded-full bg-primary-600 text-white">
                          <Check className="size-3" />
                        </span>
                      )}
                      <div className="mb-2 flex gap-1">
                        {palette.swatches.map((color) => (
                          <div
                            key={color}
                            className="h-8 flex-1 first:rounded-s-md last:rounded-e-md"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {palette.name}
                      </p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ColorPicker
                  name={t('designSystem.colorNames.primary')}
                  label={designSystem.primaryColor ?? '#334155'}
                  value={designSystem.primaryColor ?? '#334155'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, primaryColor: v }))}
                />
                <ColorPicker
                  name={t('designSystem.colorNames.primaryForeground')}
                  label={designSystem.primaryForeground ?? '#FFFFFF'}
                  value={designSystem.primaryForeground ?? '#FFFFFF'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, primaryForeground: v }))}
                />
                <ColorPicker
                  name={t('designSystem.colorNames.accent')}
                  label={designSystem.secondaryColor ?? '#64748B'}
                  value={designSystem.secondaryColor ?? '#64748B'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryColor: v }))}
                />
                <ColorPicker
                  name={t('designSystem.colorNames.secondaryForeground')}
                  label={designSystem.secondaryForeground ?? '#FFFFFF'}
                  value={designSystem.secondaryForeground ?? '#FFFFFF'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryForeground: v }))}
                />
              </div>

              <div className="mt-8 border-t border-gray-100 pt-5 dark:border-gray-800">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {t('designSystem.semanticColors')}
                </p>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  {t('designSystem.semanticColorsDesc')}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <ColorPicker
                    name={t('designSystem.colorNames.success')}
                    label={designSystem.successColor ?? '#22C55E'}
                    value={designSystem.successColor ?? '#22C55E'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, successColor: v }))}
                  />
                  <ColorPicker
                    name={t('designSystem.colorNames.warning')}
                    label={designSystem.warningColor ?? '#EAB308'}
                    value={designSystem.warningColor ?? '#EAB308'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, warningColor: v }))}
                  />
                  <ColorPicker
                    name={t('designSystem.colorNames.error')}
                    label={designSystem.errorColor ?? '#EF4444'}
                    value={designSystem.errorColor ?? '#EF4444'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, errorColor: v }))}
                  />
                  <ColorPicker
                    name={t('designSystem.colorNames.info')}
                    label={designSystem.infoColor ?? '#3B82F6'}
                    value={designSystem.infoColor ?? '#3B82F6'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, infoColor: v }))}
                  />
                </div>
              </div>

              <div className="mt-8 border-t border-gray-100 pt-5 dark:border-gray-800">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {t('designSystem.neutralPalette')}
                </p>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  {t('designSystem.neutralPaletteDesc')}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <ColorPicker
                    name={t('designSystem.colorNames.surface')}
                    label={designSystem.surfaceColor ?? '#F8FAFC'}
                    value={designSystem.surfaceColor ?? '#F8FAFC'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, surfaceColor: v }))}
                  />
                  <ColorPicker
                    name={t('designSystem.colorNames.border')}
                    label={designSystem.borderColor ?? '#E2E8F0'}
                    value={designSystem.borderColor ?? '#E2E8F0'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, borderColor: v }))}
                  />
                  <ColorPicker
                    name={t('designSystem.colorNames.mutedText')}
                    label={designSystem.mutedTextColor ?? '#64748B'}
                    value={designSystem.mutedTextColor ?? '#64748B'}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, mutedTextColor: v }))}
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="typography"
              sectionRef={sectionRefs.typography}
              title={t('designSystem.typographyTitle')}
              description={t('designSystem.typographyDesc')}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {FONT_PAIRINGS.map((pairing) => {
                  const selected = activeFontId === pairing.id;
                  return (
                    <button
                      key={pairing.id}
                      type="button"
                      onClick={() => applyFontPairing(pairing)}
                      onMouseEnter={() => {
                        loadGoogleFontPreview(pairing.body);
                        if (pairing.heading) loadGoogleFontPreview(pairing.heading);
                      }}
                      className={cn(
                        'rounded-xl border px-4 py-3 text-start transition-colors',
                        selected
                          ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-500/10'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                      )}
                    >
                      <p
                        className="text-base font-semibold text-gray-900 dark:text-gray-100"
                        style={{ fontFamily: pairing.heading ?? pairing.body }}
                      >
                        {pairing.label.split(' / ')[0]}
                      </p>
                      <p
                        className="mt-0.5 text-sm text-gray-500"
                        style={{ fontFamily: pairing.body }}
                      >
                        {pairing.heading ? `${pairing.heading} / ${pairing.body}` : pairing.body}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 border-t border-gray-100 pt-5 dark:border-gray-800">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {t('designSystem.fontLibrary')}
                </p>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  {t('designSystem.fontLibraryDesc')}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FontFamilyPicker
                    label={t('designSystem.bodyFont')}
                    value={designSystem.fontFamily}
                    onChange={(font) =>
                      setDesignSystem((s) => ({ ...s, fontFamily: font ?? s.fontFamily }))
                    }
                  />
                  <FontFamilyPicker
                    label={t('designSystem.headingFont')}
                    value={designSystem.headingFontFamily}
                    onChange={(font) => setDesignSystem((s) => ({ ...s, headingFontFamily: font }))}
                    sameAsBodyLabel={t('designSystem.sameAsBody')}
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="visual-style"
              sectionRef={sectionRefs['visual-style']}
              title={t('designSystem.visualStyleTitle')}
            >
              <div className="space-y-5">
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {t('designSystem.prototypeTheme')}
                  </p>
                  <PillOptionGroup
                    options={tOpts('theme', THEME_OPTIONS)}
                    value={designSystem.theme ?? 'light'}
                    onChange={applyTheme}
                  />
                  <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    {t('designSystem.prototypeThemeDesc')}
                  </p>
                </div>
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {t('designSystem.appBackground')}
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <ColorPicker
                      name={t('designSystem.colorNames.appBackground')}
                      label={designSystem.appBg ?? '#FFFFFF'}
                      value={designSystem.appBg ?? '#FFFFFF'}
                      onChange={(v) => setDesignSystem((s) => ({ ...s, appBg: v }))}
                    />
                  </div>
                </div>
                <CornerRadiusSelector
                  value={designSystem.borderRadius}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, borderRadius: v }))}
                />
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {t('designSystem.density')}
                  </p>
                  <PillOptionGroup
                    options={tOpts('density', DENSITY_OPTIONS)}
                    value={designSystem.density}
                    onChange={(v) => setDesignSystem((s) => ({ ...s, density: v }))}
                  />
                </div>
                <div>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {t('designSystem.elevation')}
                  </p>
                  <PillOptionGroup
                    options={tOpts('elevation', ELEVATION_PILLS)}
                    value={designSystem.elevationStyle}
                    onChange={(v) =>
                      setDesignSystem((s) => ({
                        ...s,
                        elevationStyle: v,
                        shadows: elevationToShadows(v),
                      }))
                    }
                  />
                </div>
              </div>
            </SectionBlock>

            <SectionBlock
              id="patterns"
              sectionRef={sectionRefs.patterns}
              title={t('designSystem.platformTitle', { defaultValue: 'Platform experience' })}
              description={t('designSystem.platformDesc', {
                defaultValue:
                  'Brand, colors and type stay shared. Pick a platform to tailor its UX patterns.',
              })}
            >
              <div className="space-y-5">
                {/* Build targets (multi) — which platforms this design system generates for. */}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('designSystem.buildFor', { defaultValue: 'Build for' })}
                  </Label>
                  <p className="mb-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('designSystem.buildForHint', {
                      defaultValue: 'Platforms this design system generates for.',
                    })}
                  </p>
                  <MultiPillOptionGroup
                    options={tOpts('productType', PRODUCT_TYPE_OPTIONS)}
                    values={selectedProductTypes}
                    onToggle={toggleProductType}
                  />
                </div>

                {/* Platform tabs — the active tab drives both the editor below and the
                    live preview on the right. */}
                <div className="border-b border-gray-200 dark:border-gray-800" role="tablist">
                  <div className="-mb-px flex flex-wrap gap-1">
                    {PLATFORM_OPTIONS.map((opt) => {
                      const selected = activePlatform === opt.value;
                      const TabIcon = platformIcons[opt.value];
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          onClick={() => setActivePlatform(opt.value)}
                          className={cn(
                            'flex items-center gap-1.5 border-b-2 px-3.5 py-2 text-sm font-medium transition-colors',
                            selected
                              ? 'border-primary-500 text-primary-700 dark:border-primary-400 dark:text-primary-300'
                              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                          )}
                        >
                          <TabIcon className="size-4 shrink-0" />
                          {platformLabel(opt.value)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Active platform's UX patterns (the tab panel). */}
                <div role="tabpanel">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('designSystem.platformPatternsTitle', {
                      platform: platformLabel(activePlatform),
                      defaultValue: `${platformLabel(activePlatform)} UX patterns`,
                    })}
                  </p>
                  <p className="mb-4 mt-0.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                    {t('designSystem.patternsDesc')}
                  </p>

                  {activePlatform === 'web_app' && (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {t('designSystem.patternGroups.creatingEditing')}
                        </p>
                        <div className="space-y-2">
                          <PatternGroup
                            label={t('designSystem.patternGroups.addNew')}
                            options={tOpts('formPattern', FORM_PATTERN_OPTIONS)}
                            value={ux.addPattern}
                            onChange={(v) => setUx('addPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.editItem')}
                            options={tOpts('formPattern', FORM_PATTERN_OPTIONS)}
                            value={ux.editPattern}
                            onChange={(v) => setUx('editPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.deleteConfirm')}
                            options={tOpts('deletePattern', DELETE_PATTERN_OPTIONS)}
                            value={ux.deletePattern}
                            onChange={(v) => setUx('deletePattern', v)}
                          />
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {t('designSystem.patternGroups.browsingFinding')}
                        </p>
                        <div className="space-y-2">
                          <PatternGroup
                            label={t('designSystem.patternGroups.listDisplay')}
                            options={tOpts('listPattern', LIST_PATTERN_OPTIONS)}
                            value={ux.listPattern}
                            onChange={(v) => setUx('listPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.search')}
                            options={tOpts('searchPattern', SEARCH_PATTERN_OPTIONS)}
                            value={ux.searchPattern}
                            onChange={(v) => setUx('searchPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.filters')}
                            options={tOpts('filterPattern', FILTER_PATTERN_OPTIONS)}
                            value={ux.filterPattern}
                            onChange={(v) => setUx('filterPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.detailView')}
                            options={tOpts('detailPattern', DETAIL_PATTERN_OPTIONS)}
                            value={ux.detailPattern}
                            onChange={(v) => setUx('detailPattern', v)}
                          />
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {t('designSystem.patternGroups.shellStates')}
                        </p>
                        <div className="space-y-2">
                          <PatternGroup
                            label={t('designSystem.patternGroups.navigation')}
                            options={tOpts('navPattern', NAV_PATTERN_OPTIONS)}
                            value={ux.navPattern}
                            onChange={(v) => setUx('navPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.containerWidth')}
                            options={tOpts('containerWidth', CONTAINER_WIDTH_OPTIONS)}
                            value={ux.containerWidth}
                            onChange={(v) => setUx('containerWidth', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.loadingState')}
                            options={tOpts('loadingPattern', LOADING_PATTERN_OPTIONS)}
                            value={ux.loadingPattern}
                            onChange={(v) => setUx('loadingPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.patternGroups.emptyState')}
                            options={tOpts('emptyStatePattern', EMPTY_STATE_PATTERN_OPTIONS)}
                            value={ux.emptyStatePattern}
                            onChange={(v) => setUx('emptyStatePattern', v)}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activePlatform === 'mobile' && (
                    <div className="space-y-4">
                      <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                        {t('designSystem.mobileIntro', {
                          defaultValue:
                            'Pick a native experience. We apply the matching platform conventions automatically.',
                        })}
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {MOBILE_OS_OPTIONS.map((os) => {
                          const selected = mobileOs === os.value;
                          return (
                            <button
                              key={os.value}
                              type="button"
                              onClick={() => setMobileOs(os.value)}
                              aria-pressed={selected}
                              className={cn(
                                'flex items-start gap-3 rounded-xl border p-4 text-start transition-colors',
                                selected
                                  ? 'border-blue-400 bg-blue-50/60 dark:border-blue-500/40 dark:bg-blue-500/10'
                                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                              )}
                            >
                              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
                                {os.value === 'ios' ? (
                                  <AppleLogo className="size-5" />
                                ) : (
                                  <AndroidLogo className="size-5" />
                                )}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {t(`designSystem.opt.mobileOs.${os.value}.label`, {
                                      defaultValue: os.label,
                                    })}
                                  </span>
                                  {selected && <Check className="size-4 shrink-0 text-blue-500" />}
                                </span>
                                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                                  {t(`designSystem.opt.mobileOs.${os.value}.desc`, {
                                    defaultValue: os.description,
                                  })}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="border-b border-gray-200 bg-gray-50/60 px-4 py-2.5 text-xs font-medium text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
                          {t('designSystem.mobileApplied', {
                            defaultValue: `Applied automatically for ${mobileOs === 'ios' ? 'iOS' : 'Android'}`,
                          })}
                        </div>
                        {MOBILE_CONVENTIONS[mobileOs].map((row, idx, arr) => (
                          <div
                            key={row.rowKey}
                            className={cn(
                              'flex items-center justify-between px-4 py-2.5 text-sm',
                              idx < arr.length - 1 &&
                                'border-b border-gray-100 dark:border-gray-800'
                            )}
                          >
                            <span className="text-gray-500 dark:text-gray-400">
                              {t(`designSystem.mobileConv.${row.rowKey}`, {
                                defaultValue: row.label,
                              })}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                              {row.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activePlatform === 'website' && (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {t('designSystem.websiteGroups.structureNav', {
                            defaultValue: 'Structure & navigation',
                          })}
                        </p>
                        <div className="space-y-2">
                          <PatternGroup
                            label={t('designSystem.websiteGroups.navigation', {
                              defaultValue: 'Navigation',
                            })}
                            options={tOpts('websiteNav', WEBSITE_NAV_OPTIONS)}
                            value={websiteUx.navPattern}
                            onChange={(v) => setWebsiteUx('navPattern', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.websiteGroups.contentLayout', {
                              defaultValue: 'Content layout',
                            })}
                            options={tOpts('websiteContentLayout', WEBSITE_CONTENT_LAYOUT_OPTIONS)}
                            value={websiteUx.contentLayout}
                            onChange={(v) => setWebsiteUx('contentLayout', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.websiteGroups.primaryCta', {
                              defaultValue: 'Primary CTA',
                            })}
                            options={tOpts('websitePrimaryCta', WEBSITE_PRIMARY_CTA_OPTIONS)}
                            value={websiteUx.primaryCta}
                            onChange={(v) => setWebsiteUx('primaryCta', v)}
                          />
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-4 dark:border-gray-800 dark:bg-gray-900/20">
                        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {t('designSystem.websiteGroups.contentDiscovery', {
                            defaultValue: 'Content & discovery',
                          })}
                        </p>
                        <div className="space-y-2">
                          <PatternGroup
                            label={t('designSystem.websiteGroups.heroStyle', {
                              defaultValue: 'Hero style',
                            })}
                            options={tOpts('websiteHero', WEBSITE_HERO_OPTIONS)}
                            value={websiteUx.heroStyle}
                            onChange={(v) => setWebsiteUx('heroStyle', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.websiteGroups.listDisplay', {
                              defaultValue: 'List display',
                            })}
                            options={tOpts('websiteListDisplay', WEBSITE_LIST_DISPLAY_OPTIONS)}
                            value={websiteUx.listDisplay}
                            onChange={(v) => setWebsiteUx('listDisplay', v)}
                          />
                          <PatternGroup
                            label={t('designSystem.websiteGroups.search', {
                              defaultValue: 'Search',
                            })}
                            options={tOpts('websiteSearch', WEBSITE_SEARCH_OPTIONS)}
                            value={websiteUx.searchPattern}
                            onChange={(v) => setWebsiteUx('searchPattern', v)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </SectionBlock>
          </div>

          <div className="space-y-2 border-t border-gray-200 pt-4 xl:hidden dark:border-gray-800">
            <Button
              variant="primary"
              size="default"
              onClick={onSave}
              disabled={saving}
              className="w-full gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('designSystem.saving')}
                </>
              ) : (
                <>
                  {isDirty && <UnsavedChangesDot size="sm" />}
                  <RefreshCw className="size-4" />
                  {t('designSystem.updateDs')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              disabled={saving}
              className="w-full"
            >
              {t('designSystem.reset')}
            </Button>
          </div>
        </div>
      </div>

      <aside className="hidden w-[540px] shrink-0 flex-col border-s border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900 xl:flex">
        <DesignSystemLivePreview
          designSystem={designSystem}
          logoUrl={previewUrl}
          className="min-h-0 flex-1 rounded-none border-0"
        />
        <div className="flex gap-6 shrink-0 space-y-0 border-t border-gray-100 p-4 dark:border-gray-800">
          <Button
            variant="primary"
            size="default"
            onClick={onSave}
            disabled={saving}
            className="flex-1 gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('designSystem.saving')}
              </>
            ) : (
              <>
                {isDirty && <UnsavedChangesDot size="sm" />}
                <RefreshCw className="size-4" />
                {t('designSystem.updateDs')}
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={onReset} disabled={saving}>
            {t('designSystem.reset')}
          </Button>
        </div>
      </aside>
    </div>
  );
}

export default DesignSystemPageLayout;
